import { CREATE_BLOG_COMMENT } from 'lib/mutations';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import toastr from 'toastr';
import main from 'utils/mutationWrapper';

import { PrimarySubmitButton } from '@/design-system/buttons';

export const StyledCommentForm = styled.div`
  .instruction {
    opacity: 0.6;
    font-size: 1.3rem;
  }

  .comment-box,
  .meta,
  .checkbox {
    margin-bottom: 1em;
  }

  .comment-box {
    textarea {
      width: 100%;
      min-height: 150px;
    }
  }

  label {
    font-weight: 600;
  }

  .input {
    border-radius: 10px;
    outline: none;
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 0.5em;

    &:focus {
      border: 1px solid rgba(0, 0, 0, 0.7);
    }
  }

  .meta {
    .field {
      label {
        margin-right: 1em;
      }

      &:not(:last-child) {
        margin-bottom: 1em;
      }
    }
  }

  .checkbox {
    display: flex;
    align-items: center;

    .input {
      margin-right: 0.5em;
    }

    label {
      font-size: 1.25rem;
    }
  }

  .button {
    text-transform: none;
  }
`;

const initialFormState = {
  fullname: '',
  email: '',
  body: '',
  storeCommentAuthorInfo: false
};

export const CommentForm = ({ auth, onSubmit, articleId, commentId }) => {
  const [cookies] = useCookies([]);
  const storedCommentAuthorInfoInJSON = localStorage.getItem('commentAuthorInfo');
  const storedCommentAuthorInfo = storedCommentAuthorInfoInJSON
    ? JSON.parse(storedCommentAuthorInfoInJSON)
    : null;
  const [formState, setFormState] = useState(initialFormState);
  const [isPosting, setIsPosting] = useState();

  useEffect(() => {
    if (!auth && storedCommentAuthorInfo) {
      setFormState((prevState) => {
        return {
          ...prevState,
          fullname: storedCommentAuthorInfo.fullname,
          email: storedCommentAuthorInfo.email
        };
      });
    }
  }, []);

  const handleInputChange = ({ target }) => {
    setFormState((prevState) => {
      return {
        ...prevState,
        [target.name]: target.value
      };
    });
  };

  const handleSubmit = async () => {
    if (isPosting) return;
    setIsPosting(true);

    try {
      const { createBlogComment } = await main(
        CREATE_BLOG_COMMENT,
        {
          input: {
            fullname: formState.fullname,
            email: formState.email,
            body: formState.body,
            articleId,
            commentId
          }
        },
        cookies
      );

      if (formState.storeCommentAuthorInfo) {
        localStorage.setItem(
          'commentAuthorInfo',
          JSON.stringify({
            fullname: formState.fullname,
            email: formState.email
          })
        );
      }

      if (typeof onSubmit === 'function') {
        onSubmit({
          ...createBlogComment,
          user: auth
            ? {
                fullname: `${auth.firstName} ${auth.lastName}`,
                email: auth.email,
                imageUrl: auth.imageUrl
              }
            : null
        });
      }

      setFormState(initialFormState);
    } catch (error) {
      toastr.error(error.message || 'Unable to post comment at this time. Please retry later.');
    }

    setIsPosting(false);
  };

  return (
    <StyledCommentForm>
      <p className="instruction">
        Your email address will not be published. Required fields are marked *
      </p>

      <div className="form">
        <div className="comment-box">
          <textarea
            name="body"
            className="input"
            onChange={handleInputChange}
            value={formState.body}></textarea>

          {!auth && storedCommentAuthorInfo?.fullname && (
            <p>
              Posting as <span>{storedCommentAuthorInfo.fullname}</span>
            </p>
          )}
        </div>

        {!auth && !storedCommentAuthorInfo?.fullname && (
          <>
            <div className="meta">
              <div className="field">
                <label htmlFor={`fullname-${commentId || articleId}`}>Name*</label>
                <input
                  name="fullname"
                  className="input"
                  id={`fullname-${commentId || articleId}`}
                  onChange={handleInputChange}
                  value={formState.fullname}
                />
              </div>

              <div className="field">
                <label htmlFor={`email-${commentId || articleId}`}>Email*</label>
                <input
                  name="email"
                  className="input"
                  id={`email-${commentId || articleId}`}
                  type="email"
                  onChange={handleInputChange}
                  value={formState.email}
                />
              </div>
            </div>

            <div className="field checkbox">
              <input
                className="input"
                name="storeCommentAuthorInfo"
                id={`store-details-${commentId || articleId}`}
                type="checkbox"
                onChange={handleInputChange}
                value={formState.body}
              />
              <label htmlFor={`store-details-${commentId || articleId}`}>
                Save my name and email in this browser for the next time I comment.
              </label>
            </div>
          </>
        )}

        <PrimarySubmitButton className="button" onClick={handleSubmit}>
          {isPosting ? 'Please wait...' : 'Post comment'}
        </PrimarySubmitButton>
      </div>
    </StyledCommentForm>
  );
};
